/* eslint-disable max-lines */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { lastValueFrom, map, Observable } from 'rxjs';
import { expEnvironmentRef } from '../../injection-tokens/environment.injection-token';
import { Endpoints, EnvironmentRef } from '../../../environments/interfaces/';
import { LoggerService } from '../logger/logger.service';
import { Role, RolesPermissionsPayload } from '../../components/roles-permissions';
import { AdminCompanyDetails, ApproveBusinessAdminPayload, BusinessAccountRequestLabel, EditAdminCompanyDetailsAemLabels, RejectBusinessAccountPayload } from '../../components/business-account-request';
import {
	AddMoveEquipmentLabel,
	MyFleetTableSearchFilterPayload,
	RemoveEquipmentPayload,
	MoveTruckPayload,
	AddTruckPayload,
	ChangeRequestListTablePayload,
	ChangeRequest,
	MyFleetWidgetApiPayload,
	VerifyTruckPayload,
	ReportTruckIssue,
	AddBulkEquipmentLabel,
	BulkEquipmentPayload,
	RemoveBulkEquipmentPayload,
	ReviewListLabels
} from '../../components/myfleet';
import { ApiResponse } from '../../definitions/api-response.interface';
import {
	ConnectedDevicePayload,
	EquipmentDetailsPayload,
	EquipmentImagePayload,
	FinanceTabDataPayload,
	GtsAddressConversionPayload,
	ImpactDetectionPayload,
	KeyHoursPayload,
	MyInsightsTab,
	ServiceHistoryPayload,
	VehicleUtilizationPayload
} from '../../components/myfleet/sub-modules/equipment-detail';
import { BusinessRegistrationFlowSubmitModalLabels, RegistrationState, SaveUserPayload, SubmitBusinessPayload } from '../../components/registration';

import { ApproveUserResponse, ApproveUsersPayload, LatestUserIdPayload, RejectUserResponse, RejectUsersPayload, UserListPayload, UserListStatusPayload } from '../../components/user-management/definitions/user-management.interface';
import { AssignUnassignLocationPayload, UpdatePayload, UserDetailState, UserSaveChangesPayload } from '../../components/user-management/sub-modules/user-details/definitions/user-detail.interface';
import { OrderDetailsPayload, OrderListPayload, OrderManagementStateLabel, ShippingLocationPayload } from '../../components/order-management/definitions/order-management.interface';
import { DashboardLabelState, DashboardPayload, DashboardResponse, DealerSearchPayload } from '../../components/dashboard/definitions/dashboard.interface';
import { AccountSettingsStateAemLabel, CompanyLocationPayload, CompanyDetailsPayload, LocationDetailsPayload, ProfileDetailsSavePayload, ValidateRemoveLocPayload, UpdatePrimaryLocationPayload, DomainPayload, UpdateDomainPayload, VerifyDomain } from '../../components/account-settings/definitions/account-settings.interface';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { EquipmentOrderDetailState } from '../../components/order-management/sub-modules/equipment-order-details/definitions/equipment-order-details.interface';
import { DeletePayload, NotificationList, NotificationState, UpdateNotificationList } from '../../components/notifications';
import { RemoveEquipmentDetails } from '../../components/myfleet/sub-modules/equipment-detail/definitions/remove-equipment.interface';
import { AddBulkLocationLabel, AddbulkLocationListPayload, AddbulkLocationTablePayload, AddNewLocationState, AssignedUserDataPayLoad, AssignUserSearchPayload, AssignUsersPayload, UnAssignUsersPayload } from '../../components/location-management/sub-modules/add-new-location/definitions/add-new-location.interface';
import { AddLocationPayload, AssignedLocation, CancelRenewalPayload, KeyValuePair, LeaveConfirmModalState, LoggedInLocation, UnreadNotificationPayload } from '../shared/shared-interface';
import { WidgetPayload } from '../../components/service-management/components/service-management-page/sub-modules/service-management-widgets';
import { ServiceOrderDetailsPayload, ServiceOrderDetailState } from '../../components/service-management/components/service-management-page/sub-modules/service-order-detail/definitions/service-order-detail.interface';
import { ServiceManagementAEMLabel } from '../../components/service-management/components/service-management-page';
import { Locations, ServiceManagementServiceHistoryPayload } from '../../components/service-management/components/service-management-page/sub-modules/service-management-list-table';
import { RoutePermissionGroup, RoutePermissions } from '../../guards/permission/definitions';
import { urlPaths, jsonPaths } from './api.url';
import { RemoveLocationPayload, RemoveLocationModalsAemLabel, UpdateLocationLabelInterface } from '../../components/account-settings/definitions/remove-locations.interface';
import { ChangeLangPayload, ShellData, SubmitTnc, SwitchAccountPayload, SwitchRole, UserPreferncePayload } from '../../components/shell/shell.interface';
import { AddNewUserPayload, AddUserResponse, AddUserState } from '../../components/user-management/sub-modules/add-new-user/add-user/definitions/add-user.interface';
import { BillingAddressPayload, DownloadPDFPayload, MyQuotesLabelState, MyQuotesPayload } from '../../components/my-quotes';
import { MyInsightsLabelState, MyInsightsListPayload, WeeklyImpactDetectionPayload } from '../../components/my-insights';
import { InviteUserState } from '../../components/invite-users/definitions/invite-users.interface';
import { ImpersonationPayload } from '../../components/customer-support/definition/customer-support.interface';
import { LocationAccessPayload } from '../../components/location-management/definitions/location-management.interface';
import { ContactUsPayload } from '../../components/contact-us';
import { SubOverviewListPayload, SubDefaultPayload, SubscriptionContents, SubPaymentDetailPayload } from '../../components/my-insights/subscription-management/definitions';
@Injectable({
	providedIn: 'root'
})
export class ApiService
{
	public endpoints: Endpoints = this.environmentRef.endpoints;
	location: KeyValuePair = { id: '', key: '', value: '' };
	constructor(
		private readonly httpClient: HttpClient,
		private readonly loggerService: LoggerService,
		private readonly storage: LocalStorageService,
		@Inject(expEnvironmentRef) readonly environmentRef: EnvironmentRef
	) { }

	public getUserRegistrationStatus(): Promise<ApiResponse>
	{
		const path: string = `${this.endpoints.auth.routes.auth.validUser}`;
		const requestObservable: Observable<ApiResponse> =
			this.httpClient.get<ApiResponse>(path);
		return lastValueFrom(requestObservable);
	}
	public getRolesPermissionsData(payload: RolesPermissionsPayload): Observable<ApiResponse>
	{
		const path: string = `${this.endpoints.roles.routes.roles.getRoles}`;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	public saveCustomRole(role: Role): Observable<ApiResponse>
	{
		const path: string = `${this.endpoints.roles.routes.roles.getRoles.addRoles}`;
		return this.httpClient.post<ApiResponse>(path, role);
	}
	public updateCustomRole(role: Role): Observable<ApiResponse>
	{
		const path: string = `${this.endpoints.roles.routes.roles.getRoles.update}`;
		return this.httpClient.patch<ApiResponse>(path, role);
	}

	public getRemoveRoleState(id: string): Observable<ApiResponse>
	{
		const path: string =
			`${this.endpoints.roles.routes.roles.getRoles.getRemoveRoleState}/` + id;
		return this.httpClient.get<ApiResponse>(path);
	}

	public removeRole(id: string): Observable<ApiResponse>
	{
		const path: string =
			`${this.endpoints.roles.routes.roles.getRoles.delete}` + id;
		return this.httpClient.delete<ApiResponse>(path);
	}

	public removeNotification( payload: DeletePayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + `${urlPaths.deleteNotification}`;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public downloadInvoicePdf(payload:unknown)
	{
		payload = {
			invoiceNumber: payload,
			format: 'pdf'
		};
		const path: string = this.environmentRef.baseUrl + `${urlPaths.downloadInvoicePdf}`;
		return this.httpClient.post<ApiResponse>(path,  payload );
	}
	public getAverageAgeCardData(payload: MyFleetWidgetApiPayload): Observable<ApiResponse>
	{
		const url = this.endpoints.auth.baseUrl + urlPaths.averageAgeOfEquipment;
		const headers = { 'content-type': 'application/json' };
		return this.httpClient.post<ApiResponse>(url, payload, {
			headers: headers
		});
	}
	public getCountFinancedCardData(payload: MyFleetWidgetApiPayload): Observable<ApiResponse>
	{
		const url = this.endpoints.auth.baseUrl + urlPaths.countFinancedData;
		const headers = { 'content-type': 'application/json' };

		return this.httpClient.post<ApiResponse>(url, payload, {
			headers: headers
		});
	}
	public getNumberOfUnitsData(payload: unknown): Observable<ApiResponse>
	{
		const url = this.endpoints.auth.baseUrl + urlPaths.numberOfUnits;
		const headers = { 'content-type': 'application/json' };
		return this.httpClient.post<ApiResponse>(url, payload, {
			headers: headers
		});
	}
	public updateLocationLabelInStorage(payload: UpdateLocationLabelInterface)
	{
		const locations = this.storage.getLoggedInUserLocation();
		if (locations)
		{
			const parsedLocations = JSON.parse(locations);
			parsedLocations.locations.forEach(
				(location: LoggedInLocation) =>
				{
					if(location.locationId === payload.locationId)
					{
						location.locationLabel = payload.label;
					}
				}
			);
			this.storage.setLoggedInUserLocation(this.storage.striginifyJSON(parsedLocations));
		}
	}
	downloadSearchOrFilter(payload: MyFleetTableSearchFilterPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.myfleetDownload;
		return this.httpClient.post(path, payload, {
			responseType: 'blob'
		});
	}
	myFleetList(payload: MyFleetTableSearchFilterPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.myfleetSearch;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	truckToReview(payload: MyFleetTableSearchFilterPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.trucksToReview;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public getEquipmentTypeFilterValues(locationIds: string[]): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.equipmentTypes;
		const payload = { locationIDs: locationIds };
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public getRepairTypeFilterValues()
	{
		const path = this.environmentRef.baseUrl + urlPaths.repairTypeData;
		const payload = { equipmentID: '' };
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	public getMyFleetAemLabels(): Observable<ApiResponse>
	{
		const url = `${this.endpoints.myfleetlabels.routes.myfleet.myfleetlabels}`;
		return this.httpClient.get<ApiResponse>(url);
	}

	public getRemoveEquipmentAemLabels(): Observable<RemoveEquipmentDetails>
	{
		const path: string = jsonPaths.removeEquipmentAemLabels;
		return this.httpClient.get<RemoveEquipmentDetails>(path);
	}

	public removeEquipment(payload: RemoveEquipmentPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.removeEquipment;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	public getConfirmLeavePageLabels(): Observable<LeaveConfirmModalState>
	{
		const path: string = jsonPaths.confirmLeavePageLabels;
		return this.httpClient.get<LeaveConfirmModalState>(path);
	}

	public getEquipmentData(
		payload: EquipmentDetailsPayload, equipmentVIN: string
	): Observable<ApiResponse>
	{
		const url: string =
			this.environmentRef.baseUrl + urlPaths.equipmentData + equipmentVIN;
		return this.httpClient.post<ApiResponse>(url, payload);
	}

	public getEquipmentImage(payload: EquipmentImagePayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.equipmentImage ;
		return this.httpClient.post<ApiResponse>(url, payload);
	}

	public getEquipmentFinanceData(payload: FinanceTabDataPayload, equipmentVIN: string): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.equipmentFinanceData + equipmentVIN;;
		return this.httpClient.post<ApiResponse>(url, payload);
	}

	public getEquipmentServiceHistoryData(
		payload: ServiceHistoryPayload
	): Observable<ApiResponse>
	{
		const url: string =
			this.environmentRef.baseUrl + urlPaths.serviceHistoryTable;
		return this.httpClient.post<ApiResponse>(url, payload);
	}

	public getUserManagementRolesStatus()
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.userManagementRolesStatus;
		return this.httpClient.get<ApiResponse>(url);
	}

	public getAddUserHeaders()
	{
		const url = jsonPaths.addUserAemLabels;
		return this.httpClient.get<AddUserState>(url);
	}

	public getUserDetailsHeaders()
	{
		const url = jsonPaths.userDetailsAemLabels;
		return this.httpClient.get<UserDetailState>(url);
	}

	public getUserDetails(id: string, isApproveUserScreen: boolean): Observable<ApiResponse>
	{
		let url: string;
		if (isApproveUserScreen)
		{
			url = this.endpoints.auth.baseUrl + urlPaths.getApproveScreenUserDetails;
		}
		else
		{
			url = this.endpoints.auth.baseUrl + urlPaths.getUserDetails;
		}


		const params = new HttpParams()
			.set('id', id);

		return this.httpClient.get<ApiResponse>(url, { params });
	}

	public saveUserData(payload: SaveUserPayload): Observable<ApiResponse>
	{
		const url = this.endpoints.auth.baseUrl + urlPaths.verifyInformation;
		const headers = { 'content-type': 'application/json' };
		return this.httpClient.post<ApiResponse>(url, payload, {
			headers: headers
		});
	}


	public submitRegistrationReviewOrCancel(payload: SaveUserPayload): Observable<ApiResponse>
	{
		const url = this.endpoints.auth.baseUrl + urlPaths.triggerEmailToTmhCustomerSupport;
	   const headers = { 'content-type': 'application/json' };
		return this.httpClient.post<ApiResponse>(url, payload, {
			headers: headers
		});
	}

	public saveGeneralUser(): Observable<ApiResponse>
	{
		const url = this.endpoints.auth.baseUrl + urlPaths.saveGeneralUser;
		const headers = { 'content-type': 'application/json' };
		return this.httpClient.post<ApiResponse>(url, {
			headers: headers
		});
	}

	public saveIndividualUser(): Observable<ApiResponse>
	{
		const url = this.endpoints.auth.baseUrl + urlPaths.saveIndividualUser;
		const headers = { 'content-type': 'application/json' };
		return this.httpClient.post<ApiResponse>(url, {
			headers: headers
		});
	}

	public getRegistrationFormLabels(): Observable<ApiResponse>
	{
		const url = this.endpoints.auth.baseUrl + urlPaths.registrationFormLabels;
		return this.httpClient.get<ApiResponse>(url);
	}

	public getPendingUserRequests(company: string): Observable<ApiResponse>
	{
		const url = this.endpoints.auth.baseUrl + urlPaths.pendingUserRequest;
		return this.httpClient.get<ApiResponse>(url, { params: { company: company } });
	}

	public userMangementListTable(
		payload: UserListPayload
	): Observable<ApiResponse>
	{
		const path: string =
			this.environmentRef.baseUrl + urlPaths.UserMangementTableData;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public getOrderManagementHeaders()
	{
		const url = jsonPaths.orderManagementHeaders;
		return this.httpClient.get<OrderManagementStateLabel>(url);
	}
	public getEquipmentOrderDetailHeaders()
	{
		const url = jsonPaths.equipmentOrderDetailHeaders;
		return this.httpClient.get<EquipmentOrderDetailState>(url);
	}

	public getDashboardHeaders()
	{
		const url = jsonPaths.dashboardHeaders;
		return this.httpClient.get<DashboardLabelState>(url);
	}

	public getMyQuotesAemLabels()
	{
		const url = jsonPaths.myQuotesAemLabels;
		return this.httpClient.get<MyQuotesLabelState>(url);
	}
	public getMyInsightsTabAemLabels()
	{
		const url = jsonPaths.myInsightsTabAemLabels;
		return this.httpClient.get<MyInsightsLabelState>(url);
	}
	public getInviteUsersAemLabels()
	{
		const url = jsonPaths.inviteUsersAemLabels;
		return this.httpClient.get<InviteUserState>(url);
	}
	public getDashboardDetails(payload: DashboardPayload): Observable<DashboardResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.dashboardDetails;
		return this.httpClient.post<DashboardResponse>(path, payload);
	}

	public getAccountSettingsHeaders()
	{
		const url = jsonPaths.accountSettingsHeaders;
		return this.httpClient.get<AccountSettingsStateAemLabel>(url);
	}

	public getCompanyDetails(payload: CompanyDetailsPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.companyDetails;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	public updatePrimaryLocation(payload: UpdatePrimaryLocationPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.updatePrimaryLocation;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	public getCompanyLocation(payload: CompanyLocationPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.getCompanyLocation;
		return this.httpClient.post<ApiResponse>(url, payload);
	}

	public getAddBulkLocationList(payload: AddbulkLocationListPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.getAddBulkLocationList;
		return this.httpClient.post<ApiResponse>(url, payload);
	}

	public addBulkLocationFromTable(payload: AddbulkLocationTablePayload)
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.addBulkLocationUpload;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	
	public getAddNewLocationHeaders()
	{
		const url = jsonPaths.addNewLocationHeaders;
		return this.httpClient.get<AddNewLocationState>(url);
	}

	public userMangementListStatusChange(
		payload: UserListStatusPayload
	): Observable<ApiResponse>
	{
		const path: string =
			this.environmentRef.baseUrl + urlPaths.userManagementListStatus;
		return this.httpClient.patch<ApiResponse>(path, payload);
	}
	public getLatestUserStatus(
		payload: LatestUserIdPayload
	): Observable<ApiResponse>
	{
		const path: string =
			this.environmentRef.baseUrl + urlPaths.getLatestUserStatus;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public getUserManagementAEMHeaders()
	{
		const url = this.environmentRef.baseUrl + urlPaths.userManagementAEMHeaders;
		return this.httpClient.get<ApiResponse>(url);
	}

	public notificationList(
		payload: NotificationList
	): Observable<ApiResponse>
	{
		const path: string =
			this.environmentRef.baseUrl + urlPaths.notificationsList;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	public updateNotificationList(
		payload: UpdateNotificationList
	): Observable<ApiResponse>
	{
		const path: string =
			this.environmentRef.baseUrl + urlPaths.updateNotificationList;
		return this.httpClient.patch<ApiResponse>(path, payload);
	}
	public getCurrentLocation(locationId: string)
	{
		const url = jsonPaths.currentLocation;
		this.loggerService.log(locationId);
		return this.httpClient.get<AssignedLocation>(url);
	}

	public getEquipmentOrderDetails(payload: OrderDetailsPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.orderDetails;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public getAddMoveEquipmentLabel()
	{
		const url = jsonPaths.addMoveEquipmentLabel;
		return this.httpClient.get<AddMoveEquipmentLabel>(url);
	}
	public getAddBulkEquipmentLabel()
	{
		const url = jsonPaths.addBulkEquipmentLabel;
		return this.httpClient.get<AddBulkEquipmentLabel>(url);
	}
	public getAddBulkLocationLabel()
	{
		const url = jsonPaths.addBulkLocationLabels;
		return this.httpClient.get<AddBulkLocationLabel>(url);
	}
	public equipmentMoveTruck(payload: MoveTruckPayload): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.moveTruck;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public equipmentNotFound(payload: ReportTruckIssue): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.reportTruckIssue;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public equipmentMyfleetAddTruck(payload: AddTruckPayload): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.addMyFleetEquipmentTruck;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	downloadUserManagement(payload: UserListPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.downloadUserList;
		return this.httpClient.post(path, payload, {
			responseType: 'blob'
		});
	}
	downloadEquipmentOrderList(payload: OrderListPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.downloadEquipmentOrderList;
		return this.httpClient.post(path, payload, {
			responseType: 'blob'
		});
	}
	downloadServiceManagementServiceHistoryTableList(payload: ServiceManagementServiceHistoryPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.downloadServiceManagementServiceHistoryTableList;
		return this.httpClient.post(path, payload, {
			responseType: 'blob'
		});
	}
	downloadManualsInfo(name: string)
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.downloadManualsInfo  + name;
		return this.httpClient.get(url, { responseType: 'blob' });
	}
	public rejectUsersAccess(
		payload: RejectUsersPayload
	): Observable<RejectUserResponse>
	{
		const path: string =
			this.environmentRef.baseUrl + urlPaths.rejectUsers;
		return this.httpClient.patch<RejectUserResponse>(path, payload);
	}
	public approveUsersAccess(payload: ApproveUsersPayload): Observable<ApproveUserResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.approveUsers;
		return this.httpClient.patch<ApproveUserResponse>(path, payload);
	}
	getRepairCodeWidgetData(widgetPayload: WidgetPayload)
	{
		const url = `${this.endpoints.serviceManagement.routes.serviceManagement.getRepairCodeData}`;
		return this.httpClient.post<ApiResponse>(url, widgetPayload);
	}
	getSpendBreakdownWidgetData(widgetPayload: WidgetPayload)
	{
		const url = `${this.endpoints.serviceManagement.routes.serviceManagement.getSpendBreakdownData}`;
		return this.httpClient.post<ApiResponse>(url, widgetPayload);
	}
	getPaymentStatusWidgetData(widgetPayload: WidgetPayload)
	{
		const url = `${this.endpoints.serviceManagement.routes.serviceManagement.getPaymentStatusData}`;
		return this.httpClient.post<ApiResponse>(url, widgetPayload);
	}

	getLoggedInUserObs(): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.loggedUser;
		return this.httpClient.get<ApiResponse>(url);
	}

	getLoggedInUserLocations(): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.loggedUserLocations;
		return this.httpClient.get<ApiResponse>(url);
	}
	getLoggedInTmhCorporateAdminDetails(): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.tmhCorporateAdminDetails;
		return this.httpClient.get<ApiResponse>(url);
	}
	public getNumberOfUnitsWidgetData(payload:DashboardPayload)
	{
		const url = this.environmentRef.baseUrl+ `${this.endpoints.dashboard.routes.dashboard.getNumberOfUnitsData}`;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getFinancedEquipmentData(payload:DashboardPayload)
	{
		const url = this.environmentRef.baseUrl+ `${this.endpoints.dashboard.routes.dashboard.getFinancedEquipmentData}`;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getAverageAgeEquipment(payload:DashboardPayload)
	{
		const url = this.environmentRef.baseUrl+ `${this.endpoints.dashboard.routes.dashboard.getAverageAgeEquipmentData}`;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getNewEquipmentOrders(payload:DashboardPayload)
	{
		const url = this.environmentRef.baseUrl+ `${this.endpoints.dashboard.routes.dashboard.getNewEquipmentOrders}`;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	getServiceManagementAemLabel()
	{
		const url = jsonPaths.serviceMangementAemLabels;
		return this.httpClient.get<ServiceManagementAEMLabel>(url);
	}
	public updateUserLocation(
		payload: AssignUnassignLocationPayload
	): Observable<ApiResponse>
	{
		const path: string =
			this.environmentRef.baseUrl + urlPaths.updateUserLocation;
		return this.httpClient.patch<ApiResponse>(path, payload);
	}
	public getServiceOrderDetailHeader()
	{
		const url = jsonPaths.serviceOrderDetails;
		return this.httpClient.get<ServiceOrderDetailState>(url);
	}
	public getOrderManagementTableData(
		payload: OrderListPayload
	): Observable<ApiResponse>
	{
		const path: string =
			this.environmentRef.baseUrl + urlPaths.OrderManagementTableData;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public getServiceManagementServiceHistoryData(
		payload: ServiceManagementServiceHistoryPayload
	): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.serviceHistoryTable;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getAssignedUsersData(
		payload: AssignedUserDataPayLoad
	): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.assignedUsersData;
		return this.httpClient.post<ApiResponse>(url, payload);
	}

	public saveUserDetailsData(payload: UserSaveChangesPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + `${urlPaths.saveUserDetailsData}`;
		return this.httpClient.patch<ApiResponse>(path, payload);
	}
	public saveUserDetails(payload: UpdatePayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + `${urlPaths.saveUserDetailsData}`;
		return this.httpClient.patch<ApiResponse>(path, payload);
	}

	public validateRemoveCompanyLocation(payload: ValidateRemoveLocPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + `${urlPaths.validateRemoveCompanyLocation}`;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	public getServiceOrderDetailData(payload: ServiceOrderDetailsPayload
	): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.serviceOrderData;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getPrefilledUserData()
	{
		const url: string =
			this.environmentRef.baseUrl + urlPaths.getPrefilledUserDetails;
		return this.httpClient.get<ApiResponse>(url);
	}

	public getRoutePermissions(group: RoutePermissionGroup): Observable<RoutePermissions | ApiResponse>
	{
		const url: string = this.environmentRef.endpoints.auth.routes.auth.permissions.category.id(group.category);
		const headers: HttpHeaders = new HttpHeaders({
			'action-id': group.action
		});
		return this.httpClient.get<ApiResponse>(url, { headers }).pipe(
			map(response => (response.statusCode === 200) ? response.data as RoutePermissions : response)
		);
	}

	public getServiceManagementEquipmentType(payload: Locations): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.serviceManagementEquipmentTypes;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	public getRemoveLocationModalsAEMLabels()
	{
		const url = jsonPaths.removeLocationModalsAEMLabels;
		return this.httpClient.get<RemoveLocationModalsAemLabel>(url);
	}

	public removeCompanyLocation(payload: RemoveLocationPayload): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.removeCompanyLocation;
		return this.httpClient.delete<ApiResponse>(path, { body: payload });
	}

	public updateLocationLabel(payload: UpdateLocationLabelInterface): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.updateLocationLabel;
		return this.httpClient.patch<ApiResponse>(path, payload);
	}

	public getLocationInformation(payload: LocationDetailsPayload)
	{
		const url = this.environmentRef.baseUrl + urlPaths.locationDetails;
		return this.httpClient.post<ApiResponse>(url, payload);
	}

	public getAssignedUsersList(payload: AssignUserSearchPayload): Observable<ApiResponse>
	{
		const url = this.endpoints.auth.baseUrl + urlPaths.companyAssignedUsers;
		const headers = { 'content-type': 'application/json' };
		return this.httpClient.post<ApiResponse>(url, payload, {
			headers: headers
		});
	}
	public getAEMdetailsTerms()
	{
		const url: string =
			this.environmentRef.baseUrl + urlPaths.aemTnCdata;
		return this.httpClient.get<ApiResponse>(url);
	}
	public submitTnC(payload: SubmitTnc)
	{
		const url: string =
			this.environmentRef.baseUrl + urlPaths.submitTnC;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getAEMdetailsPolicy()
	{
		const url: string =
			this.environmentRef.baseUrl + urlPaths.aemPolicyData;
		return this.httpClient.get<ApiResponse>(url);
	}
	public submitPolicy(payload: SubmitTnc)
	{
		const url: string =
			this.environmentRef.baseUrl + urlPaths.submitPolicy;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getAdminLocationList()
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.adminLocationList;
		return this.httpClient.get<ApiResponse>(url);
	}
	public switchAccount(payload: SwitchAccountPayload): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.switchAccount;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public saveUserProfileDetails(payload: ProfileDetailsSavePayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + `${urlPaths.saveUserProfileDetails}`;
		return this.httpClient.patch<ApiResponse>(path, payload);
	}

	public addCompanyLocation(payload: AddLocationPayload): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.addCompanyLocation;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	public updateUnassignUsersData(payload: UnAssignUsersPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.unassignUsersData;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	getCorporateConvert(): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.corporateConvert;
		return this.httpClient.get<ApiResponse>(url);
	}

	public getChangeRequestList(
		payload: ChangeRequestListTablePayload
	): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.changeRequestList;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public updateAssignUsersData(
		payload: AssignUsersPayload
	): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.assignUsersData;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	getUnreadNotificationCount(payload: UnreadNotificationPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.unreadNotificationsCount;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	addNewUser(payload: AddNewUserPayload): Observable<AddUserResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.getAddNewUser;
		return this.httpClient.post<AddUserResponse>(url, payload);
	}
	getChangeRequestAemLabels()
	{
		const url = jsonPaths.changeRequestTableAemlabels;
		return this.httpClient.get<ChangeRequest>(url);
	}
	getReviewListAemLabels()
	{
		const url = jsonPaths.reviewListTableAemLabels;
		return this.httpClient.get<ReviewListLabels>(url);
	}
	public getNotificationsAemLabels()
	{
		const url = jsonPaths.notificationsPageLabel;
		return this.httpClient.get<NotificationState>(url);
	}
	public getBusinessAccountRequestAemLabel()
	{
		const url = jsonPaths.businessAccountRequestAemLabel;
		return this.httpClient.get<BusinessAccountRequestLabel>(url);
	}
	public getServerValidationUploadFile (item: File, userId: string):Observable<ApiResponse>
	{
		const form = new FormData;
		form.append('file', item);
		const path: string = this.environmentRef.baseUrl + `${urlPaths.uploadNewLocationFile}` + userId;
		return this.httpClient.post<ApiResponse>(path, form);
	}
	public getBusinessAccountRequestData ():Observable<ApiResponse>
	{
		const url =  this.environmentRef.baseUrl + urlPaths.businessAccountRequestData;
		return this.httpClient.get<ApiResponse>(url);
	}
	public rejectBusinessAccount(payload: RejectBusinessAccountPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.rejectBaUser;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getEditAdminCompanyDetailsAemLabels()
	{
		const url = jsonPaths.editAdminCompanyDetailsAemLabels;
		return this.httpClient.get<EditAdminCompanyDetailsAemLabels>(url);
	}
	public getAdditionalLocations()
	{
		const url =  this.environmentRef.baseUrl + urlPaths.getAdditionalLocations;
		return this.httpClient.get<ApiResponse>(url);
	}
	public getBAUploadLocation (item: File):Observable<ApiResponse>
	{
		const form = new FormData;
		form.append('file', item);
		const path: string = this.environmentRef.baseUrl + `${urlPaths.uploadNewLocationFileReg}`;
		return this.httpClient.post<ApiResponse>(path, form);
	}
	public submitAdditionalLocation(payload: SubmitBusinessPayload)
	{
		const url =  this.environmentRef.baseUrl + urlPaths.submitAdditionalLocations;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getBusinessRegistrationFlowSubmitModalLabels()
	{
		const url: string = jsonPaths.businessRegistrationFlowSubmitModalLabels;
		return this.httpClient.get<BusinessRegistrationFlowSubmitModalLabels>(url);
	}
	public saveBusinessUser(): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.saveBusinessUser;
		return this.httpClient.post<ApiResponse>(url, null);
	}
	public getConnectedDeviceData(payload: ConnectedDevicePayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.connectedDeviceData ;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public downloadLocationList(tempUserID: string)
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.downloadLocationList + tempUserID;
		return this.httpClient.patch(url, null, { responseType: 'blob' });
	}
	public updateAdminCompanyDetails(payload: AdminCompanyDetails)
	{
		const url =  this.environmentRef.baseUrl + urlPaths.updateAdminDetails;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getBusinessRegistrationLocationDownload()
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.businessRegistrationLocationDownload;
		return this.httpClient.get(url, {
			responseType: 'blob'
		}
		);
	}

	public getBusinessRegistrationUploadListModalLabels()
	{
		const url: string = jsonPaths.businessRegistrationDownloadModalLabels;
		return this.httpClient.get<RegistrationState>(url);
	}
	public getInviteUserLocationDownload(bulkUser: boolean)
	{
		const url: string = this.environmentRef.baseUrl + (bulkUser ? urlPaths.bulkUserTemplate : urlPaths.inviteUserLocationDownload);
		return this.httpClient.get(url, {
			responseType: 'blob'
		}
		);
	}
	
	public getAddBulkEquipmentDownload(equipmentType: string)
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.addBulkEquipmentDownload + equipmentType;
		return this.httpClient.get(url, {
			responseType: 'blob'
		}
		);
	}

	public getAddBulkEquipmentDownloadSingleLocation(locationId: string, equipmentType: string)
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.addBulkEquipmentDownloadSingleLocation + locationId + equipmentType;
		return this.httpClient.get(url, {
			responseType: 'blob'
		}
		);
	}

	public getAddBulkLocationDownload()
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.addBulkLocationDownload;
		return this.httpClient.get(url, {
			responseType: 'blob'
		}
		);
	}

	public getInviteUserSubmit (item: File, isBulkUsers:boolean):Observable<ApiResponse>
	{
		const form = new FormData;
		form.append('file', item);
		const path: string = this.environmentRef.baseUrl +  ( isBulkUsers ? urlPaths.bulkUserInvite : urlPaths.inviteUserSubmit);
		return this.httpClient.post<ApiResponse>(path, form);
	}
	public getAddBulkEquipmentSubmit (item: File):Observable<ApiResponse>
	{
		const form = new FormData;
		form.append('file', item);
		const path: string = this.environmentRef.baseUrl + urlPaths.addBulkEquipmentUpload;
		return this.httpClient.post<ApiResponse>(path, form);
	}

	public addBulkLocationUpload(item: File):Observable<ApiResponse>
	{
		const form = new FormData;
		form.append('file', item);
		const path: string = this.environmentRef.baseUrl + urlPaths.addBulkLocationUpload;
		return this.httpClient.post<ApiResponse>(path, form);
	}
	
	public getmyInsightsAEMLabels()
	{
		const url: string = jsonPaths.myInsightsAEMLabels;
		return this.httpClient.get<MyInsightsTab>(url);
	}

	public getSubscriptionManagementAEMLabels()
	{
		const url: string = jsonPaths.subscriptionManagementAEMLabels;
		return this.httpClient.get<SubscriptionContents>(url);
	}

	public getVehicleUtilization(payload: VehicleUtilizationPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.vehicleUtilization ;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getLatestKeyHours(payload: KeyHoursPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.latestKeyHours ;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getConnectedKeyHours(payload: WeeklyImpactDetectionPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.latestConnectedKeyHours ;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getDailyErrorSummary(payload: WeeklyImpactDetectionPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.dailyErrorSummary ;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public approveBusinessAdminAccount(payload: ApproveBusinessAdminPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.approveBusinessAdmin;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getImpactDetectionWidgetData(payload: ImpactDetectionPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.weeklyImpactDetectionWidget;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getConnectedImpactDetectionWidgetData(payload: WeeklyImpactDetectionPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.connectedWeeklyImpactDetectionPayload;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getDailyUsageWidgetData(payload: WeeklyImpactDetectionPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.dailyUsageWidget;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getCoordinatesOfAssignedLoc(payload: GtsAddressConversionPayload)
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.gtsAddressConversionToCoordinates;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getEquipTelematicsVehicleData(payload: ConnectedDevicePayload)
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.equipTelematicsVehicleData;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getEquipmentQuoteTableData(
		payload: MyQuotesPayload
	): Observable<ApiResponse>
	{
		const path: string =
			this.environmentRef.baseUrl + urlPaths.equipmentQuotesTableEILData;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public getLastTenErrorCodesDataList(
		payload: ImpactDetectionPayload
	): Observable<ApiResponse>
	{
		const path: string =
			this.environmentRef.baseUrl + urlPaths.lastTenErrorCodesDataList;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public getDealerByZipCode(payload: DealerSearchPayload): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.dealerDetails;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	public getBillingAddress(payload: BillingAddressPayload): Observable<ApiResponse>
	{
		const path: string =
			this.environmentRef.baseUrl + urlPaths.billingAddressList;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	downloadMyQuotesTableList(payload: MyQuotesPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.myQuotesListDownload;
		return this.httpClient.post(path, payload, {
			responseType: 'blob'
		});
	}
	downloadQuotePDF(payload: DownloadPDFPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.myQuotesPdfDownload;
		return this.httpClient.post(path, payload, {
			responseType: 'blob'
		});
	}
	quoteContentBlock()
	{
		const url = this.environmentRef.baseUrl + urlPaths.quotepromoBanner;
		return this.httpClient.get<ApiResponse>(url);
	}
	public getMyInsightsPromoBanners(): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.myinsightsPromobanner;
		return this.httpClient.get<ApiResponse>(url);
	}
	getMyInsightsListData(payload: MyInsightsListPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.myinsightsTable;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	getSubscriptionOverviewListData(payload: SubOverviewListPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.subscriptionList;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	downloadMyInsightsTableList(payload: MyInsightsListPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.myinsightsTableListDownload;
		return this.httpClient.post(path, payload, { responseType: 'blob' });
	}
	public getShippingLocationList(payload: ShippingLocationPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.shippingLocationList;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	public getServiceManagementFilterList(payload: ShippingLocationPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.serviceManagementFilterList;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public getEquipmentOrderFilterList(payload: ShippingLocationPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.equipmentOrderFilterList;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public domainListData(payload: DomainPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.domainListData;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public updateDomainDetails(payload: UpdateDomainPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.updateDomainDetails;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public assignDomainDetails(payload: UpdateDomainPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.assignDomainDetails;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public getCustomerSupportLabels(): Observable<ApiResponse>
	{
		const url: string  = this.environmentRef.baseUrl + urlPaths.customerSupportLabel;
		return this.httpClient.get<ApiResponse>(url);
	}
	public getContactUsLabels(): Observable<ApiResponse>
	{
		const url: string  = this.environmentRef.baseUrl + urlPaths.contactUsLabel;
		return this.httpClient.get<ApiResponse>(url);
	}
	public startImpersonationSession(payload: ImpersonationPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.startImpersonation;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	public contactUsTicket(payload: ContactUsPayload): Observable<ApiResponse>
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.contactUsTicket;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	getLoggedInTmhCustomerSupportDetails(): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.tmhCustomerSupportDetails;
		return this.httpClient.get<ApiResponse>(url);
	}
	getValidImpersonateUserDetails(payload: ImpersonationPayload): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.validateImpersonation;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	getLocationLabels(): Observable<ApiResponse>
	{
		const url: string = this.environmentRef.baseUrl + urlPaths.locationLabels;
		return this.httpClient.get<ApiResponse>(url);
	}
	getUnassignedLocations(): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.unassignedLocations;
		return this.httpClient.get<ApiResponse>(url);
	}
	setLocationAccessManagement(payload: LocationAccessPayload): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.accessLocationManagement;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	getServiceHistoryCount(): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.serviceHistoryCount;
		return this.httpClient.get<ApiResponse>(url);
	}
	verifyDomain(payload: VerifyDomain): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.verifyDomain;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	getZendeskToken(): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.zendeskToken;
		return this.httpClient.get<ApiResponse>(url);
	}
	getContactForm(payload: ImpersonationPayload ): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.contactForm;
		return this.httpClient.post<ApiResponse>(url, payload);
	}
	getEquipmentOptions(): Observable<ApiResponse>
	{
		const url = this.environmentRef.baseUrl + urlPaths.equipmentOptions;
		return this.httpClient.get<ApiResponse>(url);
	}
	verifyEquipment(payload: VerifyTruckPayload): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.verifyEquipment;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	submitUser(payload: AssignUsersPayload): Observable<ApiResponse>
	{
		const path = this.environmentRef.baseUrl + urlPaths.submituser;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	moveBulkEquipment(payload: BulkEquipmentPayload)
	{
		const path = this.environmentRef.baseUrl + urlPaths.moveBulkEquipment;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	removeBulkEquipment(payload: RemoveBulkEquipmentPayload)
	{
		const path = this.environmentRef.baseUrl + urlPaths.removeBulkEquipment;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	getShellData()
	{
		const url = jsonPaths.shellDataLabels;
		return this.httpClient.get<ShellData>(url);
	}
	removeReminder(payload: UserPreferncePayload)
	{
		const path = this.environmentRef.baseUrl + urlPaths.removeReminder;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	downloadReviewList(payload: MyFleetTableSearchFilterPayload)
	{
		const path: string = this.environmentRef.baseUrl + urlPaths.reviewListDownload;
		return this.httpClient.post(path, payload, {
			responseType: 'blob'
		});
	}
	cancelSubscriptionRenewal(payload: CancelRenewalPayload)
	{
		const path = this.environmentRef.baseUrl + urlPaths.cancelSubscriptionRenewal;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	downloadNotificationReport(payload: string)
	{
		const url = this.environmentRef.baseUrl + urlPaths.downloadSubscriptionReport + payload;
		return this.httpClient.get(url, { responseType: 'blob' });
	}

	switchRole(payload: SwitchRole)
	{
		const path = this.environmentRef.baseUrl + urlPaths.switchRole;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	getSubscriptionPlanDetail(payload: SubDefaultPayload)
	{
		const path = this.environmentRef.baseUrl + urlPaths.subscriptionPlanDetail;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	getsubscriptionAccountSummary(payload: SubDefaultPayload)
	{
		const path = this.environmentRef.baseUrl + urlPaths.subscriptionAccountSummary;
		return this.httpClient.post<ApiResponse>(path, payload);
	}

	downloadAccountSummary(payload: SubOverviewListPayload)
	{
		const path = this.environmentRef.baseUrl + urlPaths.downloadAccountSummary;
		return this.httpClient.post(path, payload, { responseType: 'blob' });
	}

	downloadAllSubscriptions(payload: SubOverviewListPayload)
	{
		const path = this.environmentRef.baseUrl + urlPaths.downloadAllSubscriptions;
		return this.httpClient.post(path, payload, { responseType: 'blob' });
	}

	downloadSubscriptionAddress(payload: SubOverviewListPayload)
	{
		const path = this.environmentRef.baseUrl + urlPaths.downloadSubscriptionAddress;
		return this.httpClient.post(path, payload, { responseType: 'blob' });
	}

	getsubscriptionPaymentDetails(payload: SubPaymentDetailPayload)
	{
		const path = this.environmentRef.baseUrl + urlPaths.subscriptionPaymentDetails;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
	changeLanguage(payload: ChangeLangPayload)
	{
		const path = this.environmentRef.baseUrl + urlPaths.changeLanguage;
		return this.httpClient.post<ApiResponse>(path, payload);
	}
}

